// Port of Flutter's roomsScreen.dart.
//
//   AppBar   avatar · location name · "<Name> <Surname>"
//   body     common-devices strip, then the room list
//   room row ButtonColor card (elevation 4, radius 12) with a location pin,
//            the room name, an "Add: <ADD1>" subtitle and an ↑ chevron
//   FAB      Add Common Device · Add Room

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Card, ListItemButton, ListItemIcon, ListItemText, Typography, useTheme,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowUpwardSharpIcon from '@mui/icons-material/ArrowUpwardSharp';
import AddIcon from '@mui/icons-material/Add';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ProfileTitleBar } from '../components/MobileAppBar';
import CommonDevicesStrip from '../components/CommonDevicesStrip';
import CircleMenu from '../components/CircleMenu';
import AddRoomDialog from '../components/AddRoomDialog';
import UpdateCodeDialog from '../../shared/UpdateCodeDialog';
import { useApp } from '../../../core/state/AppContext';
import { getRooms, getCommonDevices } from '../../../core/api/api';

export default function MobileRoomsScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { locationId } = useParams();
  const { state } = useLocation();
  const {
    user, userId, locations, toggleFavourite, canUpdateFirmware,
  } = useApp();

  const [rooms, setRooms] = useState(null);   // null = still loading
  const [common, setCommon] = useState([]);
  const [addRoomOpen, setAddRoomOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const redirectedRef = useRef(false);

  const locationName = state?.locationName
    || locations.find((l) => String(l.LocationID) === String(locationId))?.LocationName
    || 'Location';

  const load = useCallback(async () => {
    if (!userId || !locationId) return;
    const [rs, cs] = await Promise.all([
      getRooms(userId, locationId).catch(() => null),
      getCommonDevices(userId, locationId).catch(() => null),
    ]);
    setCommon(Array.isArray(cs?.data) ? cs.data : []);
    setRooms(Array.isArray(rs?.data) ? rs.data : []);
  }, [userId, locationId]);

  useEffect(() => { load(); }, [load]);

  // Flutter's _initialRender: a location with a single room drops straight into
  // that room's devices, so the user isn't asked to tap through a list of one.
  useEffect(() => {
    if (!rooms || redirectedRef.current) return;
    if (rooms.length === 1) {
      redirectedRef.current = true;
      const r = rooms[0];
      navigate(`/room/${r.RoomID}/devices`, {
        replace: true,
        state: { roomName: r.RoomName, locationName, locationId },
      });
    }
  }, [rooms, navigate, locationName, locationId]);

  const openDevice = (d) => navigate(`/device/${d.UniqueNumber}`, { state: { device: d } });

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <ProfileTitleBar
        title={locationName}
        subtitle={`${user?.Name || ''} ${user?.Surname || ''}`.trim()}
      />

      {/* PINNED. Two independent scroll areas, as in the Dart (a fixed row above
          an Expanded list): common devices stay on screen and scroll sideways in
          their own container, while only the room list scrolls vertically. */}
      <CommonDevicesStrip
        devices={common}
        onOpen={openDevice}
        onToggleFavourite={async (d, fav) => { await toggleFavourite(d, fav); load(); }}
      />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {rooms == null ? (
          <Box sx={{ textAlign: 'center', p: 2 }}>
            <Typography sx={{ color: c.text, fontSize: 20 }}>Loading...</Typography>
          </Box>
        ) : (
          rooms.map((r) => (
            <Card
              key={r.RoomID}
              elevation={4}
              sx={{ bgcolor: c.button, borderRadius: '12px', mx: 2, my: 1 }}
            >
              <ListItemButton
                sx={{ p: 1.5, borderRadius: '12px' }}
                onClick={() => navigate(`/room/${r.RoomID}/devices`, {
                  state: { roomName: r.RoomName, locationName, locationId },
                })}
              >
                <ListItemIcon sx={{ color: c.text, minWidth: 40 }}>
                  <LocationOnIcon />
                </ListItemIcon>
                <ListItemText
                  primary={r.RoomName || 'No Room Name'}
                  secondary={`Add: ${r.ADD1 ?? 'Unknown'}`}
                  primaryTypographyProps={{ sx: { color: c.text, fontSize: 15, fontWeight: 700 } }}
                  secondaryTypographyProps={{ sx: { color: c.text, fontSize: 14 } }}
                />
                <ArrowUpwardSharpIcon sx={{ color: c.text }} />
              </ListItemButton>
            </Card>
          ))
        )}
      </Box>

      <CircleMenu
        items={[
          {
            icon: <AddIcon />,
            title: 'Add Common Device',
            onClick: () => navigate(`/add-device?locationId=${locationId}&roomId=0`),
          },
          {
            icon: <MeetingRoomIcon />,
            title: 'Add Room',
            onClick: () => setAddRoomOpen(true),
          },
          // Location-wide firmware push — owner / super admin only.
          ...(canUpdateFirmware ? [{
            icon: <SystemUpdateAltIcon />,
            title: 'Update Code (Location)',
            onClick: () => setUpdateOpen(true),
          }] : []),
        ]}
      />

      <UpdateCodeDialog
        open={updateOpen}
        scope={{ type: 'location', id: locationId, name: locationName }}
        onClose={() => setUpdateOpen(false)}
      />

      <AddRoomDialog
        open={addRoomOpen}
        locationId={locationId}
        onClose={() => setAddRoomOpen(false)}
        onAdded={() => { setAddRoomOpen(false); load(); }}
      />
    </Box>
  );
}
